import { type ClipboardEvent, useCallback } from 'react';

import type { GroupBase, InputProps } from 'react-select';

/**
 * Custom hook to configure input behavior for a react-select component,
 * specifically handling paste events.
 *
 * @template Option - The option type.
 * @template IsMulti - Boolean indicating if multiple selections are allowed.
 * @template Group - The group type, extending GroupBase<Option>.
 *
 * @param params - An object containing selectProps:
 * @param params.selectProps.canPaste - Optional boolean to enable or disable paste functionality.
 * @param params.selectProps.onOptionsPasted - Optional callback invoked with pasted text data.
 *
 * @returns An object with:
 *   - canPaste: Indicates if paste is enabled.
 *   - onPaste: Event handler for the paste event, which prevents the default paste behavior
 *     and calls onOptionsPasted with the pasted text.
 */
export const useInputConfiguration = <Option, IsMulti extends boolean, Group extends GroupBase<Option>>({
  selectProps: { canPaste = false, onOptionsPasted },
}: Pick<InputProps<Option, IsMulti, Group>, 'selectProps'>) => {
  const onPaste = useCallback(
    (e: ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();

      onOptionsPasted?.(e.clipboardData.getData('text/plain'));
    },
    [onOptionsPasted]
  );

  return {
    canPaste,
    onPaste,
  };
};
